import React, { FC, useCallback, useRef } from "react";
import CustomsDeclarationV2 from "components/CustomDeclaration/CustomsDeclarationV2";
import {
  Content,
  ContentWrapper,
  Heading,
} from "containers/RightPanel/RightPanel.styles";
import { ButtonColor, ButtonType, ButtonVariant } from "enums/Button";
import { useFormik } from "formik";
import { List } from "immutable";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import { ExpectedItemsPanelProps } from "types/ExpectedForms/ExpectedItemsPanel";
import { CustomSenderErrorMsg } from "../../../../components/Addresses/components/AddressForm.styles";
import Button from "../../../../components/Button/Button";
import Tooltip from "../../../../components/Tooltip/Tooltip";
import useFetchOnceEffect from "../../../../hooks/useFetchOnceEffect";
import {
  closeRightPanel,
  openRightPanel,
} from "../../../../reduxFolder/reducers/rightPanel";
import {
  createCustomsDeclarationForExpectedItem,
  createExpectedItem,
  deleteExpectedItem,
  getCustomsDeclarationsForExpectedItem,
  importCustomsDeclaration,
  setSelectedExpectedItemId,
  updateExpectedItem,
} from "../../../../reduxFolder/reducers/storage";
import {
  selectedExpectedItemCustomsDeclarations,
  storageSelectExpectedShops,
} from "../../../../reduxFolder/selectors/storageSelectors";
import Tab from "../../../RightPanel/components/Tab";
import TabsView from "../../../RightPanel/components/TabsView";
import { useDemoHint } from "../../../Storage/components/DemoShipments/useDemoHint";
import ExpectedServicesButtons from "../ExpectedServiceButtons/ExpectedServicesButtons";
import {
  ButtonsWrapper,
  Container,
  ContentContainer,
  FormWrapper,
  Spinner,
  Title,
} from "./ExpectedItemPanel.styles";
import { expectedItemsFormik } from "./expectedItemsFormHelper";
import ExpectedParcelForm from "./ExpectedParcelForm";

const ExpectedItemPanel: FC<ExpectedItemsPanelProps> = React.memo(
  ({
    panelData,
    openRightPanel,
    closeRightPanel,
    shops,
    deleteExpectedItem,
    getCustomsDeclarationsForExpectedItem,
    createCustomsDeclarationForExpectedItem,
    selectedExpectedItemCustomsDeclarations,
    createExpectedItem,
    updateExpectedItem,
    setSelectedExpectedItemId,
  }) => {
    const { t } = useTranslation("common");
    const isDemo = !!panelData.get("isDemo");
    const showDemoHint = useDemoHint();
    // Demo rows are browse-only: swap mutations for a hint toast that resolves
    // so the formik submit chain still settles.
    const demoStub = useCallback(() => {
      showDemoHint();
      return Promise.resolve();
    }, [showDemoHint]);

    const formProps = useFormik(
      expectedItemsFormik(
        t,
        panelData,
        shops,
        createExpectedItem,
        isDemo ? demoStub : updateExpectedItem,
        openRightPanel,
        setSelectedExpectedItemId,
      ),
    );

    const { handleSubmit, isSubmitting, values } = formProps;

    const isEditForm = panelData.get("isEditForm");
    const isCustomsDataTab = panelData.get("isCustomsDataTab");
    const formRef = useRef(null);

    const handleDelete = () => {
      if (isDemo) {
        showDemoHint();
        return;
      }
      deleteExpectedItem(values.id).then(() => {
        closeRightPanel();
      });
    };

    useFetchOnceEffect(isEditForm && !isDemo, function () {
      return getCustomsDeclarationsForExpectedItem(values.id);
    });

    const openServicePanel = useCallback(
      (requestType: string) => {
        if (isDemo) {
          showDemoHint();
          return;
        }
        openRightPanel(
          "EXPECTED_SERVICE_REQUEST",
          panelData
            .set("service_requests", panelData.get("service_requests", List()))
            .set("requestType", requestType),
        );
      },
      [openRightPanel, panelData, isDemo, showDemoHint],
    );

    const openPackOptions = useCallback(() => {
      if (isDemo) {
        showDemoHint();
        return;
      }
      openRightPanel("EXPECTED_PACK_OPTIONS", panelData);
    }, [openRightPanel, panelData, isDemo, showDemoHint]);

    return (
      <ContentWrapper>
        <Heading>
          <Title>{t("expectedParcel.expectedParcelTitle")}</Title>
        </Heading>
        <TabsView
          openTab={
            isCustomsDataTab ? t("parcels.customsData") : t("parcels.details")
          }
        >
          <Tab title={t("parcels.details")}>
            <ContentContainer>
              <Container>
                <Spinner isActive={false} />
                <FormWrapper ref={formRef} onSubmit={handleSubmit}>
                  <ExpectedParcelForm {...formProps} shops={shops} />
                  {isEditForm && (
                    <ExpectedServicesButtons
                      expectedItem={panelData}
                      openServicePanel={openServicePanel}
                      openPackOptions={openPackOptions}
                    />
                  )}
                  <ButtonsWrapper>
                    {isEditForm && (
                      <Button
                        color={ButtonColor.Red}
                        onClick={() => handleDelete()}
                      >
                        {t("common.remove")}
                      </Button>
                    )}
                    <Button
                      isLoading={isSubmitting}
                      disabled={isSubmitting}
                      type={ButtonType.Submit}
                      color={ButtonColor.Primary}
                      variant={ButtonVariant.Filled}
                    >
                      {t("common.saveChanges")}
                    </Button>
                  </ButtonsWrapper>
                </FormWrapper>
              </Container>
            </ContentContainer>
          </Tab>
          <Tab
            title={t("parcels.customsData")}
            tabHeader={
              isEditForm ? (
                t("parcels.customsData")
              ) : (
                <Tooltip
                  body={
                    <CustomSenderErrorMsg>
                      {t("parcels.notFinishedShowRequiredFieldsError")}
                    </CustomSenderErrorMsg>
                  }
                >
                  {t("parcels.customsData")}
                </Tooltip>
              )
            }
            disabled={!isEditForm}
          >
            <Content>
              <CustomsDeclarationV2
                itemId={values.id}
                declarations={
                  isDemo
                    ? panelData.get("customs_declarations", List())
                    : selectedExpectedItemCustomsDeclarations
                }
                create={createCustomsDeclarationForExpectedItem}
                readOnly={isDemo}
                noPadding
                isShipmentImportCustomsDeclaration
                hideFileImport
              />
            </Content>
          </Tab>
        </TabsView>
      </ContentWrapper>
    );
  },
);

const withConnect = connect(
  createStructuredSelector({
    shops: storageSelectExpectedShops,
    selectedExpectedItemCustomsDeclarations:
      selectedExpectedItemCustomsDeclarations,
  }),
  {
    openRightPanel,
    closeRightPanel,
    createExpectedItem,
    updateExpectedItem,
    deleteExpectedItem,
    getCustomsDeclarationsForExpectedItem,
    createCustomsDeclarationForExpectedItem,
    importCustomsDeclaration,
    setSelectedExpectedItemId,
  },
);

export default withConnect(ExpectedItemPanel);
